import React, {
  useState,
  useEffect
} from "react";

import { Helmet } from "react-helmet";

import {
  FaUserCheck,
  FaBell,
  FaChartLine,
  FaFingerprint,
  FaMobileAlt,
  FaCloud,
  FaArrowRight,
  FaCheckCircle,
  FaShieldAlt,
  FaClock
} from "react-icons/fa";

const Attendance = () => {
  const [screenWidth, setScreenWidth] =
    useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () =>
      setScreenWidth(
        window.innerWidth
      );

    window.addEventListener(
      "resize",
      handleResize
    );

    return () =>
      window.removeEventListener(
        "resize",
        handleResize
      );
  }, []);

  const isMobile =
    screenWidth <= 768;

  const whatsappLink =
    "https://wa.me/919254147455?text=Hi%20I%20want%20demo%20for%20Attendance%20Management%20Software";

  const features = [
    {
      icon: <FaUserCheck />,
      title:
        "Real-Time Attendance Tracking",
      desc: "Track student attendance instantly with smart cloud attendance software."
    },

    {
      icon: <FaFingerprint />,
      title:
        "Biometric Attendance System",
      desc: "Connect fingerprint and face recognition devices for accurate attendance."
    },

    {
      icon: <FaBell />,
      title:
        "Instant Parent Notifications",
      desc: "Automatically send absent, late and leave alerts to parents."
    },

    {
      icon: <FaChartLine />,
      title:
        "Attendance Reports & Analytics",
      desc: "Generate daily, monthly and yearly attendance reports instantly."
    },

    {
      icon: <FaMobileAlt />,
      title:
        "Mobile Attendance App",
      desc: "Teachers can mark attendance directly from mobile devices."
    },

    {
      icon: <FaCloud />,
      title:
        "Cloud Attendance Software",
      desc: "Secure cloud-based attendance management with real-time access."
    }
  ];

  return (
    <div style={styles.page}>
      <Helmet>
        <title>
          Attendance Management
          Software for Schools |
          Gurukul ERP
        </title>

        <meta
          name="description"
          content="Best attendance management software for schools with biometric attendance system, cloud attendance tracking, mobile attendance app, parent notifications and smart reports."
        />

        <meta
          name="keywords"
          content="
          attendance management software,
          school attendance software,
          biometric attendance software,
          attendance tracking system,
          online attendance system,
          attendance app for schools,
          cloud attendance software,
          smart attendance management,
          student attendance system,
          school ERP software
          "
        />

        <meta
          name="robots"
          content="index, follow"
        />

        <link
          rel="canonical"
          href="https://www.gurukulerp.com/attendance-management-software"
        />
      </Helmet>

      {/* HERO SECTION */}

      <section
        style={{
          ...styles.heroSection,
          flexDirection: isMobile
            ? "column"
            : "row"
        }}
      >
        {/* LEFT */}

        <div style={styles.heroLeft}>
          <div style={styles.tag}>
            #1 Smart Attendance
            Management Software
          </div>

          <h1
            style={{
              ...styles.heading,
              textAlign: isMobile
                ? "center"
                : "left"
            }}
          >
            Smart School <br />

            <span
              style={{
                color: "#38bdf8"
              }}
            >
              Attendance Software
            </span>
          </h1>

          <p
            style={{
              ...styles.subText,
              textAlign: isMobile
                ? "center"
                : "left"
            }}
          >
            Advanced attendance
            management software
            for schools with
            biometric attendance,
            mobile attendance app,
            cloud reports,
            parent notifications
            and automated
            attendance tracking.
          </p>

          <div
            style={{
              ...styles.heroBtns,
              flexDirection: isMobile
                ? "column"
                : "row"
            }}
          >
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration:
                  "none",
                width: isMobile
                  ? "100%"
                  : "auto"
              }}
            >
              <button
                style={{
                  ...styles.primaryBtn,
                  width: isMobile
                    ? "100%"
                    : "auto"
                }}
              >
                Get Free Demo
              </button>
            </a>

            <button
              style={{
                ...styles.secondaryBtn,
                width: isMobile
                  ? "100%"
                  : "auto"
              }}
            >
              Explore Features
            </button>
          </div>

          <div
            style={{
              ...styles.trustGrid,
              gridTemplateColumns:
                isMobile
                  ? "1fr"
                  : "repeat(3,1fr)"
            }}
          >
            <div style={styles.trustCard}>
              <FaCheckCircle />

              <span>
                100% Digital
                Attendance
              </span>
            </div>

            <div style={styles.trustCard}>
              <FaShieldAlt />

              <span>
                Secure Cloud Backup
              </span>
            </div>

            <div style={styles.trustCard}>
              <FaClock />

              <span>
                Real-Time Reports
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT */}

        <div style={styles.heroRight}>
          <div style={styles.dashboardCard}>
            <div
              style={styles.dashboardTop}
            >
              <div>
                <h3>
                  Attendance Dashboard
                </h3>

                <span
                  style={{
                    color: "#94a3b8"
                  }}
                >
                  Live School
                  Analytics
                </span>
              </div>

              <div style={styles.live}></div>
            </div>

            <div style={styles.statsGrid}>
              <div style={styles.statBox}>
                <h2>1,250</h2>
                <p>Students</p>
              </div>

              <div style={styles.statBox}>
                <h2>96%</h2>
                <p>Present</p>
              </div>

              <div style={styles.statBox}>
                <h2>48</h2>
                <p>Absent</p>
              </div>

              <div style={styles.statBox}>
                <h2>24</h2>
                <p>Late</p>
              </div>
            </div>

            <div style={styles.chartWrap}>
              {[75, 95, 60, 85, 70].map(
                (item, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.chartBar,
                      height: `${item}%`
                    }}
                  ></div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}

      <section style={styles.featureSection}>
        <div style={styles.sectionTop}>
          <span style={styles.smallTag}>
            FEATURES
          </span>

          <h2
            style={
              styles.sectionHeading
            }
          >
            Smart Attendance
            Management Features
          </h2>

          <p style={styles.sectionSub}>
            Complete school
            attendance software
            designed for modern
            schools and educational
            institutes.
          </p>
        </div>

        <div
          style={{
            ...styles.featureGrid,
            gridTemplateColumns:
              isMobile
                ? "1fr"
                : "repeat(auto-fit,minmax(300px,1fr))"
          }}
        >
          {features.map(
            (item, index) => (
              <div
                key={index}
                style={
                  styles.featureCard
                }
              >
                <div
                  style={styles.iconBox}
                >
                  {item.icon}
                </div>

                <h3>{item.title}</h3>

                <p>{item.desc}</p>
              </div>
            )
          )}
        </div>
      </section>

      {/* SEO CONTENT */}

      <section style={styles.seoSection}>
        <h2 style={styles.seoHeading}>
          Best Attendance Management
          Software for Schools
        </h2>

        <p style={styles.contentPara}>
          Gurukul ERP provides
          advanced attendance
          management software for
          schools, colleges and
          educational institutes.
          Our attendance software
          includes biometric
          attendance system,
          attendance tracking
          software, cloud attendance
          reports and online
          attendance management.
        </p>

        <p style={styles.contentPara}>
          Teachers can mark
          attendance using desktop,
          tablet or mobile
          attendance app while
          parents receive real-time
          attendance alerts and SMS
          notifications instantly.
        </p>

        <div
          style={{
            ...styles.contentGrid,
            gridTemplateColumns:
              isMobile
                ? "1fr"
                : "repeat(2,1fr)"
          }}
        >
          <div style={styles.contentCard}>
            <h3>
              Online Attendance
              Management
            </h3>

            <p>
              Mark attendance from
              mobile phones, tablets
              and desktop systems.
            </p>
          </div>

          <div style={styles.contentCard}>
            <h3>
              Biometric Attendance
              Integration
            </h3>

            <p>
              Connect fingerprint and
              face attendance devices
              easily.
            </p>
          </div>

          <div style={styles.contentCard}>
            <h3>
              Attendance Reports
            </h3>

            <p>
              Generate attendance
              reports with detailed
              analytics instantly.
            </p>
          </div>

          <div style={styles.contentCard}>
            <h3>
              Parent Notification
              System
            </h3>

            <p>
              Send automatic
              attendance alerts and
              updates to parents.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}

      <section style={styles.ctaSection}>
        <h2>
          Upgrade Your School
          Attendance System Today
        </h2>

        <p>
          Start using smart
          attendance management
          software for your school.
        </p>

        <a
          href={whatsappLink}
          target="_blank"
          rel="noreferrer"
          style={styles.ctaBtn}
        >
          Book Free Demo

          <FaArrowRight />
        </a>
      </section>

      {/* MOBILE STICKY BUTTON */}

    </div>
  );
};

const styles = {
  page: {
    background: "#020617",
    color: "#fff",
    fontFamily:
      "'Inter', sans-serif",
    overflow: "hidden"
  },

  heroSection: {
    display: "flex",
    alignItems: "center",
    justifyContent:
      "space-between",
    gap: "80px",
    padding: "120px 8%",
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top,#0f172a,#020617)"
  },

  heroLeft: {
    flex: 1
  },

  heroRight: {
    flex: 1,
    display: "flex",
    justifyContent: "center"
  },

  tag: {
    display: "inline-block",
    background:
      "rgba(59,130,246,0.15)",
    border:
      "1px solid rgba(59,130,246,0.3)",
    padding: "10px 18px",
    borderRadius: "50px",
    color: "#60a5fa",
    marginBottom: "25px",
    fontWeight: "700"
  },

  heading: {
    fontWeight: "900",
    lineHeight: "1.1",
    marginBottom: "25px",
    fontSize:
      "clamp(42px,7vw,78px)"
  },

  subText: {
    color: "#94a3b8",
    fontSize: "18px",
    lineHeight: "1.9",
    maxWidth: "650px"
  },

  heroBtns: {
    display: "flex",
    gap: "18px",
    marginTop: "35px"
  },

  primaryBtn: {
    background:
      "linear-gradient(90deg,#2563eb,#06b6d4)",
    color: "#fff",
    border: "none",
    padding: "18px 36px",
    borderRadius: "16px",
    fontWeight: "800",
    cursor: "pointer",
    fontSize: "16px"
  },

  secondaryBtn: {
    background: "transparent",
    border:
      "1px solid rgba(255,255,255,0.2)",
    color: "#fff",
    padding: "18px 36px",
    borderRadius: "16px",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "16px"
  },

  trustGrid: {
    display: "grid",
    gap: "18px",
    marginTop: "50px"
  },

  trustCard: {
    background:
      "rgba(255,255,255,0.05)",
    border:
      "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },

  dashboardCard: {
    width: "100%",
    maxWidth: "560px",
    background:
      "rgba(15,23,42,0.75)",
    backdropFilter: "blur(18px)",
    border:
      "1px solid rgba(255,255,255,0.08)",
    borderRadius: "32px",
    padding: "35px"
  },

  dashboardTop: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems: "center",
    marginBottom: "35px"
  },

  live: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    background: "#22c55e"
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(2,1fr)",
    gap: "18px"
  },

  statBox: {
    background:
      "rgba(255,255,255,0.05)",
    padding: "24px",
    borderRadius: "22px",
    textAlign: "center"
  },

  chartWrap: {
    height: "220px",
    display: "flex",
    alignItems: "flex-end",
    gap: "14px",
    marginTop: "35px"
  },

  chartBar: {
    flex: 1,
    borderRadius:
      "12px 12px 0 0",
    background:
      "linear-gradient(180deg,#38bdf8,#2563eb)"
  },

  featureSection: {
    padding: "100px 8%"
  },

  sectionTop: {
    textAlign: "center",
    marginBottom: "60px"
  },

  smallTag: {
    color: "#38bdf8",
    fontWeight: "700"
  },

  sectionHeading: {
    fontSize:
      "clamp(34px,5vw,58px)",
    fontWeight: "900",
    margin: "18px 0"
  },

  sectionSub: {
    color: "#94a3b8",
    maxWidth: "800px",
    margin: "0 auto",
    lineHeight: "1.9"
  },

  featureGrid: {
    display: "grid",
    gap: "30px"
  },

  featureCard: {
    background:
      "rgba(255,255,255,0.05)",
    border:
      "1px solid rgba(255,255,255,0.08)",
    borderRadius: "28px",
    padding: "35px"
  },

  iconBox: {
    width: "70px",
    height: "70px",
    borderRadius: "20px",
    background:
      "linear-gradient(135deg,#2563eb,#06b6d4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    marginBottom: "20px"
  },

  seoSection: {
    padding: "100px 8%",
    maxWidth: "1300px",
    margin: "0 auto"
  },

  seoHeading: {
    fontSize:
      "clamp(34px,5vw,58px)",
    marginBottom: "30px",
    fontWeight: "900"
  },

  contentPara: {
    color: "#94a3b8",
    lineHeight: "2",
    fontSize: "18px",
    marginBottom: "25px"
  },

  contentGrid: {
    display: "grid",
    gap: "25px",
    marginTop: "50px"
  },

  contentCard: {
    background:
      "rgba(255,255,255,0.05)",
    border:
      "1px solid rgba(255,255,255,0.08)",
    borderRadius: "24px",
    padding: "30px"
  },

  ctaSection: {
    margin: "0 8% 80px",
    padding: "100px 30px",
    borderRadius: "40px",
    textAlign: "center",
    background:
      "linear-gradient(135deg,#2563eb,#06b6d4)"
  },

  ctaBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "30px",
    background: "#fff",
    color: "#0f172a",
    textDecoration: "none",
    padding: "18px 34px",
    borderRadius: "14px",
    fontWeight: "800"
  },

  mobileSticky: {
    position: "fixed",
    bottom: "0",
    left: "0",
    width: "100%",
    padding: "12px",
    background: "#020617",
    zIndex: "999"
  },

  mobileBtn: {
    display: "block",
    width: "100%",
    textAlign: "center",
    background:
      "linear-gradient(90deg,#2563eb,#06b6d4)",
    color: "#fff",
    padding: "16px",
    borderRadius: "14px",
    textDecoration: "none",
    fontWeight: "800"
  }
};

export default Attendance;